import { useState } from "react";
import React from "react";
import DocumentView from "./DocumentView";
import { crmAPI } from "../../../services/AdminPortal/crmAPI";
import { formatDate } from "../../../utils/utils";
import { toast, ToastContainer } from "react-toastify";
import { notificationAPI } from "../../../services/notificationAPI";

// IconWrapper component
function IconWrapper({ children, size = 16 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {React.cloneElement(children, {
        width: size,
        height: size,
        viewBox: children.props.viewBox || "0 0 16 16",
      })}
    </div>
  );
}

const SubmittedDocuments = ({
  leadId,
  application,
  documents,
  setDocuments,
}) => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const getDocumentStatus = (docType) => {
    const foundDocument = documents.find((d) => d.documentType === docType);
    return {
      submitted: !!foundDocument,
      document: foundDocument || null,
      status: foundDocument?.status || "not-uploaded",
    };
  };
  const REQUIRED_DOCUMENTS = {
    salaried: [
      { type: "AADHAR_CARD", label: "Aadhaar Card" },
      { type: "PAN_CARD", label: "PAN Card" },
      { type: "LIVE_SELFIE", label: "Live Selfie" },
      { type: "CANCELED_CHEQUE", label: "cancelled cheque" },
      { type: "BANK_STATEMENT", label: "Bank Statement* (Last 3 months)" },
      { type: "SALARY_SLIPS", label: "Salary Slip" },
    ],
    "self-employed": [
      { type: "AADHAR_CARD", label: "Aadhaar Card" },
      { type: "PAN_CARD", label: "PAN Card" },
      { type: "LIVE_SELFIE", label: "Live Selfie" },
      { type: "CANCELED_CHEQUE", label: "cancelled cheque" },
      { type: "BANK_STATEMENT", label: "Bank Statement* (Last 3 months)" },
      { type: "INCOME_TAX_RETURN", label: "Income Tax Returns* (Last 1 Year)" },
    ],
    "business-owner": [
      { type: "AADHAR_CARD", label: "Aadhaar Card" },
      { type: "PAN_CARD", label: "PAN Card" },
      { type: "LIVE_SELFIE", label: "Live Selfie" },
      { type: "CANCELED_CHEQUE", label: "cancelled cheque" },
      // { type: "BANK_STATEMENT", label: "Bank Statement" },
      { type: "GST_DOCUMENT", label: "GST Document" },
      {
        type: "COMPANY_REGISTRATION_LETTER",
        label: "Company Registration Letter",
      },
      { type: "MSME_UDYAM_LICENSE", label: "MSME Udyam License" },
      { type: "MUNICIPAL_LICENSE", label: "Municipal License" },
      { type: "RENTAL_AGREEMENT", label: "Rental Agreement" },
      { type: "INCOME_TAX_RETURN", label: "Income Tax Returns* (Last 1 Year)" },
    ],
    default: [
      { type: "AADHAR_CARD", label: "Aadhaar Card" },
      { type: "PAN_CARD", label: "PAN Card" },
      { type: "LIVE_SELFIE", label: "Live Selfie" },
      { type: "CANCELED_CHEQUE", label: "cancelled cheque" },
      { type: "BANK_STATEMENT", label: "Bank Statement* (Last 3 months)" },
      { type: "SALARY_SLIPS", label: "Salary Slip" },
    ],
  };
  const requiredDocs = {
    salaried: [
      "AADHAR_CARD",
      "PAN_CARD",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
      "BANK_STATEMENT",
      "SALARY_SLIPS",
    ],
    "self-employed": [
      "AADHAR_CARD",
      "PAN_CARD",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
      "BANK_STATEMENT",
      "INCOME_TAX_RETURN",
    ],
    "business-owner": [
      "AADHAR_CARD",
      "PAN_CARD",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
      "GST_DOCUMENT",
      // "BANK_STATEMENT",
      "COMPANY_REGISTRATION_LETTER",
      "MSME_UDYAM_LICENSE",
      "MUNICIPAL_LICENSE",
      "RENTAL_AGREEMENT",
    ],
    default: [
      "AADHAR_CARD",
      "PAN_CARD",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
      "BANK_STATEMENT",
      "SALARY_SLIPS",
    ],
  };
  // Calculate missing docs based on employment type but show all submitted docs
  const employmentType = application?.user?.employmentType || "salaried";
  const requiredDocsForType = requiredDocs[employmentType] || requiredDocs.default;
  const missingDocs = requiredDocsForType.filter(
    (docType) =>
      !documents.some((document) => document.documentType === docType)
  );

  const sendNotification = async (
    userId,
    note,
    notificationType,
    senderUserId
  ) => {
    try {
      const notification = await notificationAPI.sendNotification(
        userId,
        note,
        notificationType,
        senderUserId
      );
      toast.success(`Notification sent: ${note}`);
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Could not send notification.");
    }
  };

  const openDocumentModal = (documentId) => {
    const document = documents.find((document) => document._id === documentId);
    if (document) {
      setSelectedDocument(document);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDocument(null);
  };

  const handleVerifyDocument = async (documentId, status) => {
    try {
      const updatedDoc = await crmAPI.verifyDocument(
        leadId,
        documentId,
        status
      );
      setDocuments((prevDocuments) =>
        prevDocuments.map((document) =>
          document._id === documentId ? { ...document, status } : document
        )
      );
      toast.success("Document status updated successfully");
    } catch (error) {
      console.error("Error updating document status:", error);
      toast.error("Failed to update document status");
    }
    closeModal();
  };

  return (
    <div>
      <div className="bg-[#131313] rounded-lg p-4 mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3 sm:gap-0">
          <div className="flex items-center gap-2">
            <svg
              width="15"
              height="21"
              viewBox="0 0 15 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.2578 4.17188C14.7266 4.64062 15 5.26562 15 5.92969V18C15 19.4062 13.8672 20.5 12.5 20.5H2.5C1.09375 20.5 0 19.4062 0 18V3C0 1.63281 1.09375 0.5 2.5 0.5H9.57031C10.2344 0.5 10.8594 0.773438 11.3281 1.24219L14.2578 4.17188ZM13.125 18V6.75H10C9.29688 6.75 8.75 6.20312 8.75 5.5V2.41406H2.5C2.14844 2.41406 1.875 2.6875 1.875 3.03906V18C1.875 18.3516 2.14844 18.625 2.5 18.625H12.5C12.8125 18.625 13.125 18.3516 13.125 18ZM3.75 11.4375C3.75 10.9297 4.14062 10.5 4.6875 10.5H10.3125C10.8203 10.5 11.25 10.9297 11.25 11.4375C11.25 11.9844 10.8203 12.375 10.3125 12.375H4.6875C4.14062 12.375 3.75 11.9844 3.75 11.4375ZM10.3125 14.25C10.8203 14.25 11.25 14.6797 11.25 15.1875C11.25 15.7344 10.8203 16.125 10.3125 16.125H4.6875C4.14062 16.125 3.75 15.7344 3.75 15.1875C3.75 14.6797 4.14062 14.25 4.6875 14.25H10.3125Z"
                fill="#FFCA20"
              />
            </svg>

            <h3 className="text-white text-lg">
              {missingDocs.length === 0 ? "Submitted Documents" : "Documents"}
            </h3>
          </div>
          {missingDocs.length !== 0 ? (
            <button
              onClick={() =>
                sendNotification(
                  application?.user?._id,
                  "Please submit the required documents",
                  "Staff",
                  application?.assignedTo?._id
                )
              }
              className="text-[#FFCA20] flex"
            >
              Request All Documents
            </button>
          ) : (
            <button
            onClick={async () => {
              try {
                const JSZip = (await import("jszip")).default;
                const { saveAs } = await import("file-saver");
                const zip = new JSZip();
                if (!documents || documents.length === 0) {
                  toast.info("No documents available to add to the ZIP.");
                  return;
                }

                const applicationName =
                  application?.user?.name?.replace(/\s+/g, "_") || "applicant";
                const docTypeToLabelMap = {
                  'AADHAR_CARD': 'Aadhaar_Card',
                  'PAN_CARD': 'PAN_Card',
                  'LIVE_SELFIE': 'Live_Selfie',
                  'CANCELED_CHEQUE': 'Cancelled_Cheque',
                  'BANK_STATEMENT': 'Bank_Statement',
                  'SALARY_SLIPS': 'Salary_Slip',
                  'INCOME_TAX_RETURN': 'Income_Tax_Returns',
                  'GST_DOCUMENT': 'GST_Document',
                  'COMPANY_REGISTRATION_LETTER': 'Company_Registration_Letter',
                  'MSME_UDYAM_LICENSE': 'MSME_Udyam_License',
                  'MUNICIPAL_LICENSE': 'Municipal_License',
                  'RENTAL_AGREEMENT': 'Rental_Agreement'
                };

                let filesAddedToZip = 0;

                for (const documentItem of documents) {
                  if (documentItem && documentItem.filePath) {
                    try {
                      const response = await fetch(documentItem.filePath);
                      if (!response.ok) {
                        console.error(
                          `Failed to fetch ${documentItem.filePath}: ${response.status} ${response.statusText}`
                        );
                        toast.error(
                          `Could not download ${
                            documentItem.documentType || "a document"
                          }. Server returned ${response.status}.`
                        );
                        continue; // Skip this document and try the next one
                      }
                      const blob = await response.blob();
                      if (blob.size === 0) {
                        console.warn(
                          `Fetched empty blob for ${documentItem.filePath}`
                        );
                        toast.warn(
                          `The file for ${
                            documentItem.documentType || "a document"
                          } appears to be empty and was skipped.`
                        );
                        continue; // Skip empty blobs
                      }

                      const urlParts = documentItem.filePath.split(".");
                      const extension =
                        urlParts.length > 1
                          ? urlParts.pop().split("?")[0]
                          : "file";

                      const documentLabel =
                        docTypeToLabelMap[documentItem.documentType] ||
                        documentItem.documentType.replace(/\s+/g, "_");
                      const fileNameInZip = `${applicationName}-${documentLabel}.${extension}`;
                      zip.file(fileNameInZip, blob);
                      filesAddedToZip++;
                    } catch (fileError) {
                      console.error(
                        `Error processing file ${documentItem.filePath} for ZIP:`,
                        fileError
                      );
                      toast.error(
                        `Error processing ${
                          documentItem.documentType || "a document"
                        } for the ZIP.`
                      );
                    }
                  } else {
                    console.warn(
                      "Skipping document due to missing filePath:",
                      documentItem
                    );
                  }
                }
                toast.success("Files download initiated.");
                if (filesAddedToZip === 0) {
                  toast.error(
                    "No files could be successfully added to the ZIP. Please check document URLs or network issues."
                  );
                  return;
                }

                const content = await zip.generateAsync({ type: "blob" });
                saveAs(content, `${applicationName}_documents.zip`); // More descriptive ZIP filename
              } catch (error) {
                console.error("Error downloading documents zip:", error);
                alert("Failed to download documents zip. Please try again.");
              }
            }}
            className="text-[#FFCA20] flex items-center gap-2"
          >
            Download as ZIP
            <svg
              width="15"
              height="21"
              viewBox="0 0 15 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.2578 4.17188C14.7266 4.64062 15 5.26562 15 5.92969V18C15 19.4062 13.8672 20.5 12.5 20.5H2.5C1.09375 20.5 0 19.4062 0 18V3C0 1.63281 1.09375 0.5 2.5 0.5H9.57031C10.2344 0.5 10.8594 0.773438 11.3281 1.24219L14.2578 4.17188ZM13.125 18V6.75H10C9.29688 6.75 8.75 6.20312 8.75 5.5V2.41406H6.875V3H4.375V2.41406H2.5C2.14844 2.41406 1.875 2.6875 1.875 3.03906V18C1.875 18.3516 2.14844 18.625 2.5 18.625H12.5C12.8125 18.625 13.125 18.3516 13.125 18ZM6.875 4.25V5.5H4.375V4.25H6.875ZM6.875 6.75V8H4.375V6.75H6.875ZM6.875 9.25L8.04688 13.8203C8.35938 15.3438 7.1875 16.75 5.625 16.75C4.02344 16.75 2.85156 15.3438 3.16406 13.8203L4.375 9.25H6.875ZM6.25 14.875C6.5625 14.875 6.875 14.6016 6.875 14.25C6.875 13.9375 6.5625 13.625 6.25 13.625H5C4.64844 13.625 4.375 13.9375 4.375 14.25C4.375 14.6016 4.64844 14.875 5 14.875H6.25Z"
                fill="#FFCA20"
              />
            </svg>
            </button>
          )}
        </div>

        {/* Document Sections */}
        {(() => {
          // Get document IDs for each applicant type
          const coApplicant1DocIds = application?.coApplicants?.[0] ? 
            [application.coApplicants[0].aadharCard, application.coApplicants[0].panCard, application.coApplicants[0].passportPhoto].filter(Boolean) : [];
          const coApplicant2DocIds = application?.coApplicants?.[1] ? 
            [application.coApplicants[1].aadharCard, application.coApplicants[1].panCard, application.coApplicants[1].passportPhoto].filter(Boolean) : [];
          const guarantorDocIds = application?.guarantor ? 
            [application.guarantor.aadharCard, application.guarantor.panCard, application.guarantor.passportPhoto].filter(Boolean) : [];
          
          // Group documents by matching IDs
          const userDocs = documents.filter(doc => 
            !coApplicant1DocIds.includes(doc._id) && 
            !coApplicant2DocIds.includes(doc._id) && 
            !guarantorDocIds.includes(doc._id)
          );
          const coApplicant1Docs = documents.filter(doc => coApplicant1DocIds.includes(doc._id));
          const coApplicant2Docs = documents.filter(doc => coApplicant2DocIds.includes(doc._id));
          const guarantorDocs = documents.filter(doc => guarantorDocIds.includes(doc._id));

          const renderDocumentCard = (document) => {
            const type = document.documentType;
            const label = (() => {
              const typeLabels = {
                'AADHAR_CARD': 'Aadhaar Card',
                'PAN_CARD': 'PAN Card',
                'LIVE_SELFIE': 'Live Selfie',
                'CANCELED_CHEQUE': 'cancelled cheque',
                'BANK_STATEMENT': 'Bank Statement',
                'SALARY_SLIPS': 'Salary Slip',
                'INCOME_TAX_RETURN': 'Income Tax Returns',
                'GST_DOCUMENT': 'GST Document',
                'COMPANY_REGISTRATION_LETTER': 'Company Registration Letter',
                'MSME_UDYAM_LICENSE': 'MSME Udyam License',
                'MUNICIPAL_LICENSE': 'Municipal License',
                'RENTAL_AGREEMENT': 'Rental Agreement'
              };
              return typeLabels[type] || type.replace(/_/g, ' ');
            })();
            
            const docStatus = document.status || "pending";

            return (
              <div
                key={document._id}
                className={`bg-[#131313] border border-[#333333] rounded-lg p-4`}
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <svg
                      width="14"
                      height="19"
                      viewBox="0 0 14 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.832 4.05469C13.2539 4.47656 13.5 5.03906 13.5 5.63672V16.5C13.5 17.7656 12.4805 18.75 11.25 18.75H2.25C0.984375 18.75 0 17.7656 0 16.5V3C0 1.76953 0.984375 0.75 2.25 0.75H8.61328C9.21094 0.75 9.77344 0.996094 10.1953 1.41797L12.832 4.05469ZM11.8125 16.5V6.375H9C8.36719 6.375 7.875 5.88281 7.875 5.25V2.47266H2.25C1.93359 2.47266 1.6875 2.71875 1.6875 3.03516V16.5C1.6875 16.8164 1.93359 17.0625 2.25 17.0625H11.25C11.5312 17.0625 11.8125 16.8164 11.8125 16.5ZM7.10156 10.8398C7.3125 11.2617 7.59375 11.6133 7.94531 11.9648C8.82422 11.7891 9.45703 11.7539 9.5625 11.7539H9.66797C10.4062 11.7539 10.9688 12.3516 10.9688 13.0547C10.9688 13.793 10.4062 14.3555 9.70312 14.3555C9.52734 14.3555 9.35156 14.3203 9.21094 14.2852C8.61328 14.0742 8.05078 13.793 7.59375 13.4766C6.89062 13.6172 6.25781 13.793 5.69531 14.0391C5.27344 14.8125 4.88672 15.375 4.64062 15.7617C4.39453 16.043 4.04297 16.2188 3.65625 16.2188C3.02344 16.2188 2.49609 15.6914 2.49609 15.0586C2.49609 14.707 2.60156 14.4258 2.8125 14.1797C3.30469 13.6875 3.9375 13.2656 4.67578 12.9141C5.02734 12.2461 5.30859 11.5781 5.55469 10.875C5.09766 9.71484 5.09766 8.69531 5.09766 8.69531C5.09766 8.0625 5.625 7.5 6.25781 7.5C6.92578 7.5 7.45312 8.0625 7.45312 8.69531C7.45312 9.43359 7.24219 10.3477 7.17188 10.5234C7.13672 10.6289 7.10156 10.7344 7.10156 10.8398ZM3.86719 15.1992C4.00781 14.9883 4.28906 14.6016 4.60547 14.0039C4.18359 14.25 3.79688 14.5312 3.48047 14.8477C3.44531 14.8828 3.44531 14.9531 3.44531 15.0234C3.44531 15.3047 3.72656 15.375 3.86719 15.1992ZM6.25781 8.44922C6.11719 8.44922 6.01172 8.55469 6.01172 8.69531C6.01172 8.76562 6.04688 9.39844 6.29297 10.1719C6.36328 9.92578 6.50391 9.22266 6.50391 8.69531C6.50391 8.55469 6.39844 8.44922 6.25781 8.44922ZM5.69531 13.0195C6.22266 12.8086 6.75 12.668 7.24219 12.5625C6.89062 12.2109 6.60938 11.8242 6.36328 11.4375C6.15234 12 5.90625 12.5273 5.69531 13.0195ZM9.5625 12.7031C9.5625 12.7031 9.07031 12.7383 8.36719 12.8438C8.68359 13.0547 9.07031 13.2305 9.49219 13.3711C9.5625 13.4062 9.63281 13.4062 9.70312 13.4062C9.87891 13.4062 10.0195 13.2656 10.0195 13.0898C10.0195 12.8438 9.87891 12.7031 9.66797 12.7031H9.5625Z"
                        fill="#FFCA20"
                      />
                    </svg>

                    <div className="flex flex-col">
                      <p className="text-white text-lg">
                        {type === "BANK_STATEMENT" ? "Bank Statement*" : 
                         type === "INCOME_TAX_RETURN" ? "Income Tax Returns*" : `${label}*`}
                      </p>
                      {type === "BANK_STATEMENT" && (
                        <p className="text-gray-400 text-xs">(Last 3 months)</p>
                      )}
                      {type === "INCOME_TAX_RETURN" && (
                        <p className="text-gray-400 text-xs">(Last 1 Year)</p>
                      )}
                    </div>
                  </div>
                  {docStatus === "verified" ? (
                    <div className="flex items-center gap-1 bg-green-500/20 border border-green-500 rounded-full px-2 py-1">
                      <span className="text-xs text-green-400">Verified</span>
                      <IconWrapper size={12}>
                        <svg
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path stroke="#4ADE80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M3 8l3 3L13 5" />
                        </svg>
                      </IconWrapper>
                    </div>
                  ) : docStatus === "rejected" ? (
                    <div className="flex items-center bg-red-500/20 border border-red-500 rounded-full px-2 py-1">
                      <span className="text-xs text-red-400">Rejected</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 bg-yellow-500/20 border border-yellow-500 rounded-full px-2 py-1">
                      <span className="text-xs text-[#FFCA20]">Pending</span>
                      <IconWrapper size={12}>
                        <svg
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="8" cy="8" r="7" stroke="#FFCA20" strokeWidth="1" fill="none" />
                          <path stroke="#FFCA20" strokeWidth="1.5" strokeLinecap="round" d="M8 5v4M8 12h.01" />
                        </svg>
                      </IconWrapper>
                    </div>
                  )}
                </div>
                <>
                  <p className="text-gray-400 text-xs mb-2">
                    Submitted on {formatDate(document.uploadDate)}
                  </p>
                  <div className="flex space-x-4">
                    <button
                      className="text-[#FFCA20] text-xs"
                      onClick={() => openDocumentModal(document._id)}
                    >
                      View
                    </button>
                    <button
                      className="text-[#FFCA20] text-xs"
                      onClick={() => {
                        if (
                          document &&
                          (document.fileUrl || document.filePath)
                        ) {
                          const link = window.document.createElement("a");
                          
                          const typeLabels = {
                            'AADHAR_CARD': 'Aadhaar_Card',
                            'PAN_CARD': 'PAN_Card',
                            'LIVE_SELFIE': 'Live_Selfie',
                            'CANCELED_CHEQUE': 'Cancelled_Cheque',
                            'BANK_STATEMENT': 'Bank_Statement',
                            'SALARY_SLIPS': 'Salary_Slip',
                            'INCOME_TAX_RETURN': 'Income_Tax_Returns',
                            'GST_DOCUMENT': 'GST_Document',
                            'COMPANY_REGISTRATION_LETTER': 'Company_Registration_Letter',
                            'MSME_UDYAM_LICENSE': 'MSME_Udyam_License',
                            'MUNICIPAL_LICENSE': 'Municipal_License',
                            'RENTAL_AGREEMENT': 'Rental_Agreement'
                          };

                          const userNameForFile =
                            application?.user?.name?.replace(/\s+/g, "_") ||
                            document?.user?.name?.replace(/\s+/g, "_") ||
                            "user";
                          const documentLabelForFile =
                            typeLabels[document.documentType] ||
                            document.documentType.replace(/\s+/g, "_") ||
                            "document";

                          const fileUrlForExt =
                            document.fileUrl || document.filePath || "";
                          const urlPartsForExt = fileUrlForExt.split(".");
                          const extension =
                            urlPartsForExt.length > 1
                              ? urlPartsForExt.pop().split("?")[0]
                              : "file";

                          const filenameToUse = `${userNameForFile}-${documentLabelForFile}.${extension}`;
                          link.download = filenameToUse;

                          let downloadUrl = document.fileUrl || document.filePath;

                          if (downloadUrl.includes("/upload/")) {
                            const parts = downloadUrl.split("/upload/");
                            if (parts.length === 2) {
                              downloadUrl = `${parts[0]}/upload/fl_attachment/${parts[1]}`;
                            }
                          }

                          link.href = downloadUrl;
                          window.document.body.appendChild(link);
                          link.click();
                          window.document.body.removeChild(link);

                          toast.success("Document download initiated.");
                        } else {
                          toast.error(
                            "Document file not available for download."
                          );
                        }
                      }}
                    >
                      Download
                    </button>
                  </div>
                </>
              </div>
            );
          };

          const renderDocumentSection = (sectionDocs, sectionTitle) => {
            if (sectionDocs.length === 0) return null;
            
            return (
              <div key={sectionTitle} className="mb-6">
                <h4 className="text-[#FFCA20] text-sm font-semibold mb-3 border-b border-[#333] pb-2">
                  {sectionTitle}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {sectionDocs.map(renderDocumentCard)}
                </div>
              </div>
            );
          };

          return (
            <div>
              {renderDocumentSection(userDocs, "User Documents")}
              {application?.coApplicants?.[0] && renderDocumentSection(coApplicant1Docs, `Co-Applicant 1 Documents (${application.coApplicants[0].fullName})`)}
              {application?.coApplicants?.[1] && renderDocumentSection(coApplicant2Docs, `Co-Applicant 2 Documents (${application.coApplicants[1].fullName})`)}
              {application?.guarantor && renderDocumentSection(guarantorDocs, `Guarantor Documents (${application.guarantor.fullName})`)}
            </div>
          );
        })()}
      </div>
      <DocumentView
        documentTypeMap={{
          'AADHAR_CARD': 'Aadhaar Card',
          'PAN_CARD': 'PAN Card',
          'LIVE_SELFIE': 'Live Selfie',
          'CANCELED_CHEQUE': 'cancelled cheque',
          'BANK_STATEMENT': 'Bank Statement',
          'SALARY_SLIPS': 'Salary Slip',
          'INCOME_TAX_RETURN': 'Income Tax Returns',
          'GST_DOCUMENT': 'GST Document',
          'COMPANY_REGISTRATION_LETTER': 'Company Registration Letter',
          'MSME_UDYAM_LICENSE': 'MSME Udyam License',
          'MUNICIPAL_LICENSE': 'Municipal License',
          'RENTAL_AGREEMENT': 'Rental Agreement'
        }}
        REQUIRED_DOCUMENTS={REQUIRED_DOCUMENTS}
        application={application}
        sendNotification={sendNotification}
        document={selectedDocument || {}}
        isOpen={isModalOpen}
        onClose={closeModal}
        handleVerifyDocument={handleVerifyDocument}
      />
    </div>
  );
};

export default SubmittedDocuments;
